import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {ButtonBase} from "@mui/material";
import {useTranslation} from "react-i18next";
import {DialogComponent} from "../Dialog/Dialog.Component";
import {SelectComponent} from "../Select/Select.Component";
import TablesComponent from "../Tables/Tables.Component";
import {FailedItemsDialog} from "./FailedItems.Dialog";
import {ConfirmDeleteDialog} from "../ConfirmDelete/ConfirmDelete.Dialog";
import {
  GetBulkActionList,
  ConfirmBulkAction,
} from "../../Services/SetupsJobRequisition.Services";
import {showError} from "../../Helpers";
import {BulkOperationActionTypeEnum, BulkOperationStatusEnum} from "../../enums";
import "./OperationsTracker.Style.scss";

// Kill-switch for the auto refresh feature, set it to false to disable it.
const IS_AUTO_REFRESH_ENABLED = true;
const AUTO_REFRESH_INTERVAL_IN_SECONDS = 5;
// Stop polling a never settling operation instead of hammering the API for as
// long as the dialog stays open, ~15 minutes worth of refreshes.
const AUTO_REFRESH_MAX_ATTEMPTS = 180;
// Tolerate a couple of transient failures before giving up on auto refreshing.
const AUTO_REFRESH_MAX_CONSECUTIVE_FAILURES = 3;

// Done & failed are the only final statuses, anything else (validated included,
// since without the confirm step it moves on by itself) is still expected to
// change on its own.
const SETTLED_OPERATION_STATUSES = [
  BulkOperationStatusEnum.Done.value,
  BulkOperationStatusEnum.Failed.value,
];

const isOperationSettled = (operation) =>
  SETTLED_OPERATION_STATUSES.includes(operation.status);

// Each flow gets its own wording, so the note tells the user what the pending
// validation means for the action they actually started.
const VALIDATION_NOTE_KEY_BY_ACTION_TYPE = {
  [BulkOperationActionTypeEnum.RequestVacancy.value]:
    "Shared:bulk-request-validating-note",
  [BulkOperationActionTypeEnum.ApprovalAction.value]:
    "Shared:bulk-approve-validating-note",
};

const OPERATIONS_COLUMNS_CONFIG = [
  {key: "action_type", label: "Shared:action-type"},
  {key: "status", label: "Shared:status"},
  {key: "requested_count", label: "Shared:requested-count"},
  {key: "approved_count", label: "Shared:approved-count"},
  {key: "failed_count", label: "Shared:failed-count"},
  {key: "processed_success", label: "Shared:processed-success"},
  {key: "processed_failed", label: "Shared:processed-failed"},
  {key: "created_at", label: "Shared:created-at", isDate: true},
  {key: "updated_at", label: "Shared:updated-at", isDate: true},
];

export const OperationsTrackerDialog = ({
  isOpen,
  actionType,
  onClose,
  onOperationSettled,
  parentTranslationPath,
}) => {
  const {t} = useTranslation([parentTranslationPath, "Shared"]);
  const [selectedActionType, setSelectedActionType] = useState(
    actionType || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isAutoRefreshStopped, setIsAutoRefreshStopped] = useState(false);
  const [filter, setFilter] = useState({limit: 10, page: 1});
  const [operations, setOperations] = useState(() => ({
    results: [],
    totalCount: 0,
  }));
  const [confirmActionItem, setConfirmActionItem] = useState(null);
  const [failedItemsBulkUuid, setFailedItemsBulkUuid] = useState(null);
  const [isPageVisible, setIsPageVisible] = useState(() => !document.hidden);
  const isMountedRef = useRef(true);
  const latestRequestIdRef = useRef(0);
  const autoRefreshAttemptsRef = useRef(0);
  const autoRefreshFailuresRef = useRef(0);
  // The operations last seen unsettled, to tell when one of them settles.
  const unsettledOperationUuidsRef = useRef(new Set());

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // No point in polling a tab nobody is looking at.
  useEffect(() => {
    const onVisibilityChanged = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibilityChanged);

    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChanged);
  }, []);

  useEffect(() => {
    if (isOpen) setSelectedActionType(actionType || null);
  }, [isOpen, actionType]);

  // Any explicit user action is a fresh start for the auto refresh budget, it
  // is also how the user recovers once auto refreshing has given up.
  useEffect(() => {
    autoRefreshAttemptsRef.current = 0;
    autoRefreshFailuresRef.current = 0;
    setIsAutoRefreshStopped(false);
  }, [selectedActionType, filter]);

  const onActionTypeChanged = useCallback((value) => {
    setSelectedActionType(value);
    setFilter((items) => (items.page === 1 ? items : {...items, page: 1}));
  }, []);

  const getOperationsList = useCallback(
    async (isSilentRefresh = false) => {
      if (!selectedActionType) return;
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      if (!isSilentRefresh) setIsLoading(true);
      setIsFetching(true);
      let response;
      try {
        response = await GetBulkActionList({
          ...filter,
          action_type: selectedActionType,
        });
      } finally {
        // Only the newest request owns the flags, an older one settling late
        // must not release the gate while a newer one is still in flight.
        if (isMountedRef.current && latestRequestIdRef.current === requestId) {
          if (!isSilentRefresh) setIsLoading(false);
          setIsFetching(false);
        }
      }
      // Drop superseded responses & anything landing after the dialog closed.
      if (!isMountedRef.current || latestRequestIdRef.current !== requestId)
        return;
      if (response && response.status === 200) {
        autoRefreshFailuresRef.current = 0;
        setOperations({
          results: response.data?.results ?? [],
          totalCount: response.data?.paginate?.total ?? 0,
        });

        return;
      }
      if (isSilentRefresh) {
        // Keep the already loaded data & only give up once the endpoint has
        // failed repeatedly, so a single blip does not kill auto refreshing.
        autoRefreshFailuresRef.current += 1;
        if (
          autoRefreshFailuresRef.current >= AUTO_REFRESH_MAX_CONSECUTIVE_FAILURES
        ) {
          setIsAutoRefreshStopped(true);
          showError(t("Shared:failed-to-get-saved-data"), response);
        }

        return;
      }
      setOperations({results: [], totalCount: 0});
      showError(t("Shared:failed-to-get-saved-data"), response);
    },
    [filter, selectedActionType, t]
  );

  useEffect(() => {
    if (isOpen && selectedActionType) getOperationsList();
  }, [isOpen, selectedActionType, getOperationsList]);

  // No operations, or only done & failed ones, means there is nothing left to
  // wait for.
  const hasUnsettledOperations = useMemo(
    () => operations.results.some((item) => !isOperationSettled(item)),
    [operations.results]
  );

  // Once a followed operation is done (or failed) the items it touched have
  // moved on, so the parent is told to reload its data.
  useEffect(() => {
    const hasSettledOperations = operations.results.some(
      (item) =>
        unsettledOperationUuidsRef.current.has(item.uuid) &&
        isOperationSettled(item)
    );
    unsettledOperationUuidsRef.current = new Set(
      operations.results
        .filter((item) => !isOperationSettled(item))
        .map((item) => item.uuid)
    );
    if (hasSettledOperations && onOperationSettled) onOperationSettled();
  }, [operations.results, onOperationSettled]);

  const isAutoRefreshing =
    IS_AUTO_REFRESH_ENABLED &&
    isOpen &&
    !!selectedActionType &&
    !isAutoRefreshStopped &&
    hasUnsettledOperations;

  // Queues the next refresh only once the previous one has settled, so the
  // requests never overlap.
  useEffect(() => {
    if (!isAutoRefreshing || !isPageVisible || isFetching) return undefined;
    const timeoutId = setTimeout(() => {
      autoRefreshAttemptsRef.current += 1;
      if (autoRefreshAttemptsRef.current > AUTO_REFRESH_MAX_ATTEMPTS) {
        setIsAutoRefreshStopped(true);

        return;
      }
      getOperationsList(true);
    }, AUTO_REFRESH_INTERVAL_IN_SECONDS * 1000);

    return () => clearTimeout(timeoutId);
  }, [isAutoRefreshing, isPageVisible, isFetching, getOperationsList]);

  // Only shown while some operation is still pending. Once auto refreshing gave
  // up the list is no longer updating, so the note says so instead of asking the
  // user to wait. The generic note is the fallback for an action type without
  // its own.
  const trackerNote = useMemo(() => {
    if (!hasUnsettledOperations) return null;
    if (isAutoRefreshStopped) return t("Shared:auto-refresh-paused");
    const noteKey = VALIDATION_NOTE_KEY_BY_ACTION_TYPE[selectedActionType];
    if (noteKey) return t(noteKey);

    return t("Shared:auto-refresh-in-progress", {
      seconds: AUTO_REFRESH_INTERVAL_IN_SECONDS,
    });
  }, [hasUnsettledOperations, isAutoRefreshStopped, selectedActionType, t]);

  // A fresh auto refresh budget along with the reload, so polling picks up again
  // while anything is still pending.
  const onManualRefreshClicked = useCallback(() => {
    autoRefreshAttemptsRef.current = 0;
    autoRefreshFailuresRef.current = 0;
    setIsAutoRefreshStopped(false);
    getOperationsList();
  }, [getOperationsList]);

  const onPageIndexChanged = useCallback((newIndex) => {
    setFilter((items) => ({...items, page: newIndex + 1}));
  }, []);

  const onPageSizeChanged = useCallback((newPageSize) => {
    setFilter((items) => ({...items, page: 1, limit: newPageSize}));
  }, []);

  const headerData = useMemo(
    () => [
      {
        isHidden: false,
        input: "actions",
        label: t("Shared:actions"),
        component: (row) => (
          <div className="d-flex-v-center">
            {/* Confirm is hidden for now, the confirm logic below is kept in place.
            <ButtonBase
              className="btns theme-solid"
              disabled={row.status !== BulkOperationStatusEnum.Validated.value}
              onClick={() => setConfirmActionItem(row)}
            >
              <span>{t("Shared:confirm")}</span>
            </ButtonBase>
            */}
            <ButtonBase
              className="btns theme-solid"
              onClick={() => setFailedItemsBulkUuid(row.uuid)}
            >
              <span>{t("Shared:details")}</span>
            </ButtonBase>
          </div>
        ),
      },
      ...OPERATIONS_COLUMNS_CONFIG.map((column) => ({
        isHidden: false,
        input: column.key,
        label: t(column.label),
        isDate: column.isDate,
        component:
          column.key === "status"
            ? (row) => (
                <span className={`status-pill ${row.status} py-1 px-2`}>
                  {t(`Shared:${row.status}`)}
                </span>
              )
            : column.key === "action_type"
            ? (row) => <span>{t(`Shared:${row.action_type}`)}</span>
            : undefined,
      })),
    ],
    [t]
  );

  const dialog = (
    <DialogComponent
      titleText={"Shared:operations-tracker"}
      maxWidth="lg"
      isOpen={isOpen}
      onCloseClicked={onClose}
      dialogContent={
        <div className="operations-tracker-wrapper">
          {trackerNote && (
            <div className="auto-refresh-note d-flex-v-center mb-3">
              <span
                className={`mdi mdi-autorenew${
                  isAutoRefreshing ? " is-spinning" : ""
                }`}
              />
              <span className="px-2">{trackerNote}</span>
              {isAutoRefreshStopped && (
                <ButtonBase
                  className="btns theme-solid"
                  disabled={isFetching}
                  onClick={onManualRefreshClicked}
                >
                  <span>{t("Shared:refresh")}</span>
                </ButtonBase>
              )}
            </div>
          )}
          {/* Auto refresh only runs while something is pending, so say the rows
              are final rather than showing nothing at all. */}
          {IS_AUTO_REFRESH_ENABLED &&
            !!selectedActionType &&
            !hasUnsettledOperations &&
            operations.results.length > 0 && (
              <div className="auto-refresh-note is-complete d-flex-v-center mb-3">
                <span className="mdi mdi-check-circle-outline" />
                <span className="px-2">{t("Shared:auto-refresh-idle")}</span>
              </div>
            )}
          {!actionType && (
            <SelectComponent
              idRef="operationsTrackerActionTypeSelectRef"
              data={Object.values(BulkOperationActionTypeEnum)}
              value={selectedActionType}
              valueInput="key"
              textInput="value"
              translationPathForData="Shared:"
              placeholder={t("Shared:select-operation-type")}
              wrapperClasses="mb-3"
              onSelectChanged={onActionTypeChanged}
              parentTranslationPath={parentTranslationPath}
            />
          )}
          <TablesComponent
            isWithoutBoxWrapper
            isWithEmpty
            data={operations.results}
            headerData={headerData}
            pageIndex={filter.page - 1}
            pageSize={filter.limit}
            totalItems={operations.totalCount}
            isLoading={isLoading}
            uniqueKeyInput="uuid"
            onPageIndexChanged={onPageIndexChanged}
            onPageSizeChanged={onPageSizeChanged}
            parentTranslationPath={parentTranslationPath}
          />
        </div>
      }
      parentTranslationPath={parentTranslationPath}
    />
  );

  return (
    <>
      {dialog}
      {confirmActionItem && (
        <ConfirmDeleteDialog
          isOpen={!!confirmActionItem}
          activeItem={confirmActionItem}
          translationPath="Shared:"
          descriptionMessage="confirm-operation-description"
          extraDescription={t("Shared:confirm-operation-note")}
          extraDescriptionClasses="d-flex-center mt-2"
          successMessage="operation-confirmed-successfully"
          errorMessage="operation-confirm-failed"
          deleteApi={ConfirmBulkAction}
          apiProps={{bulk_operation_uuid: confirmActionItem.uuid}}
          onSave={() => getOperationsList()}
          isOpenChanged={() => setConfirmActionItem(null)}
          parentTranslationPath={parentTranslationPath}
        />
      )}
      {failedItemsBulkUuid && (
        <FailedItemsDialog
          isOpen={!!failedItemsBulkUuid}
          bulkOperationUuid={failedItemsBulkUuid}
          onClose={() => setFailedItemsBulkUuid(null)}
          parentTranslationPath={parentTranslationPath}
        />
      )}
    </>
  );
};

OperationsTrackerDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actionType: PropTypes.string,
  onClose: PropTypes.func,
  // When given, it is called once an operation followed by this dialog settles
  // (e.g. to reload the parent's data).
  onOperationSettled: PropTypes.func,
  parentTranslationPath: PropTypes.string,
};
